import { backend } from "@/integrations/backend/client";

export const DAILY_STREAK_BONUS_POINTS_PER_DAY = 5;
export const DAILY_STREAK_BONUS_MAX_POINTS = 50;
export const DAILY_STREAK_TIME_ZONE = "America/Sao_Paulo";

const DAILY_STREAK_BONUSES_TABLE = "daily_streak_bonuses";
const LOCAL_DAILY_STREAK_BONUSES_KEY = "lina:daily-streak-bonuses";

export interface DailyStreakBonusRecord {
  user_id: string;
  bonus_on: string;
  streak_count: number;
  points: number;
  created_at: string;
}

export interface DailyStreakBonusAwardResult {
  points: number;
  awarded: boolean;
  alreadyAwarded: boolean;
  storedLocally: boolean;
}

function dailyStreakDateParts(date = new Date()) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: DAILY_STREAK_TIME_ZONE,
    weekday: "short",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);

  const partValue = (type: string) =>
    parts.find((part) => part.type === type)?.value ?? "";

  return {
    year: partValue("year"),
    month: partValue("month"),
    day: partValue("day"),
    weekday: partValue("weekday"),
  };
}

function localDateKey(date = new Date()) {
  const { year, month, day } = dailyStreakDateParts(date);
  return `${year}-${month}-${day}`;
}

export function isDailyStreakWeekend(date = new Date()) {
  const { weekday } = dailyStreakDateParts(date);
  return weekday === "Sat" || weekday === "Sun";
}

function isWeekendDateKey(dateKey: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey);
  if (!match) return false;

  const [, year, month, day] = match;
  const dayOfWeek = new Date(
    Date.UTC(Number(year), Number(month) - 1, Number(day), 12),
  ).getUTCDay();
  return dayOfWeek === 0 || dayOfWeek === 6;
}

function bonusKey(userId: string, bonusOn: string) {
  return `${userId}|${bonusOn}`;
}

function readLocalDailyStreakBonuses() {
  if (typeof window === "undefined") return [];

  try {
    const raw = window.localStorage.getItem(LOCAL_DAILY_STREAK_BONUSES_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? (parsed.filter(
          (item) =>
            item &&
            typeof item.user_id === "string" &&
            typeof item.bonus_on === "string" &&
            typeof item.streak_count === "number" &&
            typeof item.points === "number" &&
            !isWeekendDateKey(item.bonus_on),
        ) as DailyStreakBonusRecord[])
      : [];
  } catch {
    return [];
  }
}

function writeLocalDailyStreakBonus(record: DailyStreakBonusRecord) {
  if (typeof window === "undefined") return;

  const records = readLocalDailyStreakBonuses();
  const nextRecords = [
    ...records.filter(
      (item) =>
        item.user_id !== record.user_id || item.bonus_on !== record.bonus_on,
    ),
    record,
  ];

  window.localStorage.setItem(
    LOCAL_DAILY_STREAK_BONUSES_KEY,
    JSON.stringify(nextRecords),
  );
}

function mergeDailyStreakBonuses(records: DailyStreakBonusRecord[]) {
  const byKey = new Map<string, DailyStreakBonusRecord>();

  records.forEach((record) => {
    byKey.set(bonusKey(record.user_id, record.bonus_on), record);
  });

  return Array.from(byKey.values());
}

export function dailyStreakBonusForStreak(currentStreak: number | null | undefined) {
  const streak = Math.max(0, Math.floor(Number(currentStreak) || 0));
  if (streak <= 1) return 0;
  return Math.min(
    DAILY_STREAK_BONUS_MAX_POINTS,
    (streak - 1) * DAILY_STREAK_BONUS_POINTS_PER_DAY,
  );
}

export async function awardDailyStreakBonus(
  userId: string,
  currentStreak: number | null | undefined,
): Promise<DailyStreakBonusAwardResult> {
  if (isDailyStreakWeekend()) {
    return {
      points: 0,
      awarded: false,
      alreadyAwarded: false,
      storedLocally: false,
    };
  }

  const points = dailyStreakBonusForStreak(currentStreak);
  const bonusOn = localDateKey();

  if (points <= 0) {
    return {
      points: 0,
      awarded: false,
      alreadyAwarded: false,
      storedLocally: false,
    };
  }

  const localRecords = readLocalDailyStreakBonuses();
  const alreadyAwardedLocally = localRecords.some(
    (record) => record.user_id === userId && record.bonus_on === bonusOn,
  );
  const record: DailyStreakBonusRecord = {
    user_id: userId,
    bonus_on: bonusOn,
    streak_count: Math.max(0, Math.floor(Number(currentStreak) || 0)),
    points,
    created_at: new Date().toISOString(),
  };

  try {
    const { error } = await backend
      .from(DAILY_STREAK_BONUSES_TABLE)
      .upsert(record, { onConflict: "user_id,bonus_on" });

    if (error) throw error;

    return {
      points,
      awarded: true,
      alreadyAwarded: false,
      storedLocally: false,
    };
  } catch (error) {
    console.warn("Could not persist daily streak bonus remotely:", error);

    if (!alreadyAwardedLocally) {
      writeLocalDailyStreakBonus(record);
    }

    return {
      points,
      awarded: !alreadyAwardedLocally,
      alreadyAwarded: alreadyAwardedLocally,
      storedLocally: true,
    };
  }
}

export async function loadDailyStreakBonusRecords(userIds?: string[]) {
  const normalizedUserIds = (userIds ?? []).filter(Boolean);
  const userIdSet = new Set(normalizedUserIds);
  const localRecords = readLocalDailyStreakBonuses().filter(
    (record) => userIdSet.size === 0 || userIdSet.has(record.user_id),
  );

  try {
    let query = backend
      .from(DAILY_STREAK_BONUSES_TABLE)
      .select("user_id, bonus_on, streak_count, points, created_at");

    if (normalizedUserIds.length > 0) {
      query = query.in("user_id", normalizedUserIds);
    }

    const { data, error } = await query;
    if (error) throw error;

    return {
      records: mergeDailyStreakBonuses([
        ...((data || []) as DailyStreakBonusRecord[]).filter(
          (record) => !isWeekendDateKey(record.bonus_on),
        ),
        ...localRecords,
      ]),
      storageAvailable: true,
    };
  } catch (error) {
    console.warn("Could not load daily streak bonuses:", error);
    return {
      records: localRecords,
      storageAvailable: false,
    };
  }
}

export async function loadDailyStreakBonusTotals(userIds?: string[]) {
  const { records, storageAvailable } =
    await loadDailyStreakBonusRecords(userIds);
  const totals = new Map<string, number>();

  records.forEach((record) => {
    totals.set(record.user_id, (totals.get(record.user_id) ?? 0) + record.points);
  });

  return { totals, storageAvailable };
}
